import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchUserPermissions, saveUserPermissions, updateStaffUser, deleteStaffUser } from "../pages/services/userManagementService";
import "../styles/AddForm.css";
import "../styles/BatchDetails.css";

// One user's permissions.
//
// Every switch shows its role default, so an admin can always see what they
// have changed and put it back. Only the differences are stored, which is why
// "Reset to defaults" is a real action here and not just setting values.

const Toggle = ({ checked, onChange, disabled }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled}
    onClick={() => onChange(!checked)}
    style={{
      width: 44, height: 24, borderRadius: 999, border: "none", position: "relative", flex: "0 0 auto",
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
      background: checked ? "#16a34a" : "#cbd5e1", transition: "background .18s ease",
    }}>
    <span style={{
      position: "absolute", top: 3, left: checked ? 23 : 3, width: 18, height: 18, borderRadius: "50%",
      background: "#fff", transition: "left .18s ease", boxShadow: "0 1px 3px rgba(0,0,0,.25)",
    }} />
  </button>
);

const UserPermissions = () => {
  const { userType, id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = () => {
    setLoading(true);
    fetchUserPermissions(userType, id)
      .then((d) => { setData(d); setValues(d.effective); setError(""); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [userType, id]);

  // What differs from this role's defaults right now.
  const changed = useMemo(() => {
    if (!data) return [];
    return Object.keys(values).filter((k) => values[k] !== data.defaults[k]);
  }, [values, data]);

  const dirty = useMemo(() => {
    if (!data) return false;
    return Object.keys(values).some((k) => values[k] !== data.effective[k]);
  }, [values, data]);

  const save = async () => {
    setSaving(true); setError(""); setMessage("");
    try {
      const res = await saveUserPermissions(userType, id, values);
      setData((d) => ({ ...d, effective: res.effective, overrides: res.overrides }));
      setValues(res.effective);
      setMessage("Permissions saved.");
      setTimeout(() => setMessage(""), 3000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const removeStaff = async () => {
    try { await deleteStaffUser(id); navigate("/users", { state: { successMessage: "Staff user deleted" } }); }
    catch (e) { setError(e.message); setConfirmDelete(false); }
  };

  const toggleActive = async () => {
    try {
      const next = !data.user.isActive;
      await updateStaffUser(id, { isActive: next });
      setData((d) => ({ ...d, user: { ...d.user, isActive: next } }));
      setMessage(next ? "Account activated." : "Account deactivated.");
      setTimeout(() => setMessage(""), 3000);
    } catch (e) { setError(e.message); }
  };

  if (loading) return <div className="af-page"><div className="af-card" style={{ padding: 40, textAlign: "center" }}><div className="af-spinner" style={{ margin: "0 auto 12px" }} /><p style={{ color: "#6b7280" }}>Loading…</p></div></div>;
  if (!data) return <div className="af-page"><div className="af-card" style={{ padding: 30 }}><div className="af-alert af-alert-error">{error || "User not found."}</div><button className="af-secondary-btn" onClick={() => navigate("/users")}>Back to users</button></div></div>;

  const { user, roleLabel, unrestricted, permissions } = data;
  const isStaff = ["content_engineer", "sales_executive"].includes(userType);

  // Grouped purely for readability; the grouping carries no meaning.
  const groups = permissions.reduce((acc, p) => { (acc[p.group] ||= []).push(p); return acc; }, {});

  return (
    <div className="af-page">
      <div className="af-card" style={{ maxWidth: 860 }}>
        <div className="af-header">
          <div className="af-header-icon indigo">🔐</div>
          <div className="af-header-text" style={{ flex: 1 }}>
            <h2 className="af-title">{user.name || "Unnamed user"}</h2>
            <p className="af-subtitle">
              {roleLabel}
              {user.contactNumber ? ` · ${user.contactNumber}` : ""}
              {user.emailId ? ` · ${user.emailId}` : ""}
              {!user.isActive && " · Inactive"}
            </p>
          </div>
          <button type="button" className="af-secondary-btn" onClick={() => navigate("/users")}>Back</button>
        </div>

        {message && <div className="af-alert af-alert-success" style={{ margin: "16px 32px 0" }}>{message}</div>}
        {error && <div className="af-alert af-alert-error" style={{ margin: "16px 32px 0" }}>{error}</div>}

        <div className="af-body">
          {unrestricted ? (
            <div className="af-section">
              <p style={{ color: "#475569", lineHeight: 1.7, margin: 0 }}>
                <strong>Admin accounts are not restricted by permissions.</strong> An admin is who grants and revokes
                access on this screen, so there is nothing here to switch off. To stop someone signing in, remove
                their admin account.
              </p>
            </div>
          ) : (
            <>
              <div className="af-section" style={{ paddingBottom: 6 }}>
                <p style={{ margin: 0, color: "#475569", fontSize: 13.5 }}>
                  {changed.length === 0
                    ? <>Every permission is at this role's default.</>
                    : <><strong>{changed.length}</strong> permission{changed.length === 1 ? "" : "s"} changed from the {roleLabel} default.</>}
                  {data.updatedDateTime && <> Last changed {new Date(data.updatedDateTime).toLocaleString()}{data.updatedBy ? ` by ${data.updatedBy}` : ""}.</>}
                </p>
              </div>

              {Object.entries(groups).map(([group, list]) => (
                <div className="af-section" key={group}>
                  <p className="af-section-title">{group}</p>
                  {list.map((p) => {
                    const isChanged = values[p.key] !== p.default;
                    return (
                      <div key={p.key} style={{
                        display: "flex", alignItems: "center", gap: 14, padding: "11px 12px", marginBottom: 8,
                        background: isChanged ? "#fffbeb" : "#f8fafc",
                        border: `1px solid ${isChanged ? "#fde68a" : "#e2e8f0"}`, borderRadius: 8,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13.5, color: "#0f172a" }}>{p.label}</div>
                          <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>
                            Default: {p.default ? "Allowed" : "Not allowed"}
                            {isChanged && <span style={{ color: "#b45309", fontWeight: 700 }}> · changed</span>}
                          </div>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: values[p.key] ? "#16a34a" : "#94a3b8", width: 84, textAlign: "right" }}>
                          {values[p.key] ? "Allowed" : "Not allowed"}
                        </span>
                        <Toggle checked={!!values[p.key]} onChange={(v) => setValues((s) => ({ ...s, [p.key]: v }))} />
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          )}

          {isStaff && (
            <div className="af-section">
              <p className="af-section-title">Account</p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <button type="button" className="af-secondary-btn" onClick={toggleActive}>
                  {user.isActive ? "Deactivate account" : "Activate account"}
                </button>
                <button type="button" className="af-secondary-btn" style={{ color: "#ef4444", borderColor: "#fecaca" }}
                  onClick={() => setConfirmDelete(true)}>Delete staff user</button>
              </div>
              {confirmDelete && (
                <div style={{ marginTop: 12, padding: 14, border: "1px solid #fecaca", background: "#fef2f2", borderRadius: 8 }}>
                  <p style={{ margin: "0 0 10px", color: "#991b1b", fontSize: 13.5 }}>
                    Delete <strong>{user.name}</strong> and their permission settings? This cannot be undone.
                  </p>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button type="button" className="af-secondary-btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
                    <button type="button" className="af-submit" style={{ background: "#ef4444", flex: "0 0 auto", padding: "8px 18px" }} onClick={removeStaff}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {!unrestricted && (
          <div className="af-submit-row" style={{ display: "flex", gap: 12 }}>
            <button type="button" className="af-secondary-btn" disabled={changed.length === 0}
              onClick={() => setValues(data.defaults)}>Reset to defaults</button>
            <button type="button" className="af-submit" disabled={!dirty || saving} style={{ flex: 1 }} onClick={save}>
              {saving ? "Saving…" : dirty ? "Save permissions" : "No changes to save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPermissions;
